/**
 * Hook `useEcheancesDeclaratives` (Lot F) — source unique des échéances
 * déclaratives (DRM / DS / DR) consommée par le bandeau du Hub Conformité ET par
 * le widget dashboard (DRY).
 *
 * Il croise 3 lectures légères (listes DRM/DS/DR) avec les règles de rappel
 * (`echeances.ts`) pour produire, pour chaque déclaration pertinente à
 * l'instant T, son échéance + son état d'avancement. Chaque lecture est :
 *   - gardée par sa permission (`*.read`) — masquage de confort, le backend
 *     reste la vraie garde ;
 *   - restreinte à sa saison pour la DS (août-sept.) et la DR (oct.-déc.) ;
 *   - non-réessayée (`retry: false`) et dégradée silencieusement en cas d'échec
 *     (une échéance manquante n'empêche jamais le reste de s'afficher).
 *
 * Les queryKeys sont volontairement IDENTIQUES à celles des cartes du hub →
 * react-query dédupe : aucune requête réseau en double.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { resoudreDomaineIdActif } from "../../auth/tenant-actif.js";
import { useCan } from "../../auth/use-can.js";
import { useMe } from "../../auth/use-me.js";
import { tenantQueryKey } from "../../lib/tenant-query-key.js";
import { trpc } from "../../lib/trpc.js";

import {
  ctaSelonEtat,
  echeanceDr,
  echeanceDrm,
  echeanceDs,
  echeanceUrgente,
  estFaite,
  etatDepuisStatut,
  joursRestants,
  libelleMois,
  periodeADeclarer,
  saisonDrOuverte,
  saisonDsOuverte,
  type EtatDeclaration,
} from "./echeances.js";
import { ROUTES_CONFORMITE } from "./utils.js";

/** Une échéance déclarative prête à afficher (bandeau / widget). */
export interface EcheanceDeclarative {
  /** Type de déclaration (sert de clé de rendu + de recherche depuis le hub). */
  id: "drm" | "ds" | "dr";
  /** Titre lisible (« DRM de juin 2026 »). */
  titre: string;
  /** Sous-titre explicatif court. */
  sousTitre: string;
  /** Date d'échéance (ISO `YYYY-MM-DD`). */
  echeanceIso: string;
  /** Jours restants (négatif = dépassée). */
  jours: number;
  /** Vrai dès J−7 (ou dépassée) → pastille urgente. */
  urgente: boolean;
  /** État d'avancement (absente / brouillon / transmise / néant). */
  etat: EtatDeclaration;
  /** Vrai si transmise (ou néant transmise) → sortie de l'urgence. */
  faite: boolean;
  /** Route de l'assistant correspondant. */
  route: string;
  /** Libellé du CTA adapté à l'état. */
  ctaLabel: string;
}

export interface EcheancesDeclarativesResultat {
  echeances: EcheanceDeclarative[];
  isLoading: boolean;
}

export function useEcheancesDeclaratives(): EcheancesDeclarativesResultat {
  // Date figée pour le rendu (évite un recalcul instable entre renders).
  const today = useMemo(() => new Date(), []);
  const { data: me } = useMe();
  const domaineId = resoudreDomaineIdActif(me);

  const { granted: canDrm } = useCan("drm.read");
  const { granted: canDs } = useCan("ds.read");
  const { granted: canDr } = useCan("dr.read");

  const dsOuverte = saisonDsOuverte(today);
  const drOuverte = saisonDrOuverte(today);

  const drmQuery = useQuery({
    queryKey: tenantQueryKey(domaineId, "conformiteDrm.listerDrm"),
    queryFn: () => trpc.conformiteDrm.listerDrm.query(),
    enabled: !!domaineId && canDrm,
    retry: false,
  });
  const dsQuery = useQuery({
    queryKey: tenantQueryKey(domaineId, "conformiteDs.lister"),
    queryFn: () => trpc.conformiteDs.lister.query(),
    enabled: !!domaineId && canDs && dsOuverte,
    retry: false,
  });
  const drQuery = useQuery({
    queryKey: tenantQueryKey(domaineId, "conformiteDr.lister"),
    queryFn: () => trpc.conformiteDr.lister.query(),
    enabled: !!domaineId && canDr && drOuverte,
    retry: false,
  });

  const echeances: EcheanceDeclarative[] = [];

  // ── DRM : obligation mensuelle (même sans mouvement) ────────────────────────
  if (canDrm) {
    const periode = periodeADeclarer(today);
    const drm = drmQuery.data?.drms.find(
      (d) => d.periode_annee === periode.annee && d.periode_mois === periode.mois,
    );
    const etat = etatDepuisStatut(drm?.statut, drm?.est_neant ?? false);
    const echeanceIso = echeanceDrm(periode);
    const jours = joursRestants(echeanceIso, today);
    echeances.push({
      id: "drm",
      titre: `DRM de ${libelleMois(periode.mois)} ${periode.annee}`,
      sousTitre: "Déclaration mensuelle obligatoire, même sans aucun mouvement.",
      echeanceIso,
      jours,
      urgente: echeanceUrgente(jours),
      etat,
      faite: estFaite(etat),
      route: ROUTES_CONFORMITE.drm,
      ctaLabel: ctaSelonEtat(etat),
    });
  }

  // ── DS : stock au 31/07, uniquement en saison (août → sept.) ────────────────
  if (canDs && dsOuverte) {
    const annee = today.getFullYear();
    const ds = dsQuery.data?.declarations.find((d) => d.annee === annee);
    const etat = etatDepuisStatut(ds?.statut, ds?.est_neant ?? false);
    const echeanceIso = echeanceDs(annee);
    const jours = joursRestants(echeanceIso, today);
    echeances.push({
      id: "ds",
      titre: `Déclaration de stock au 31/07/${annee}`,
      sousTitre: "Photographie de votre stock au 31 juillet (bouteilles sur lattes comprises).",
      echeanceIso,
      jours,
      urgente: echeanceUrgente(jours),
      etat,
      faite: estFaite(etat),
      route: ROUTES_CONFORMITE.ds,
      ctaLabel: ctaSelonEtat(etat),
    });
  }

  // ── DR : récolte annuelle, uniquement en saison (oct. → déc.) ───────────────
  if (canDr && drOuverte) {
    const annee = today.getFullYear();
    const dr = drQuery.data?.declarations.find((d) => d.millesime === annee);
    const etat = etatDepuisStatut(dr?.statut, dr?.est_neant ?? false);
    const echeanceIso = echeanceDr(annee);
    const jours = joursRestants(echeanceIso, today);
    echeances.push({
      id: "dr",
      titre: `Déclaration de récolte ${annee}`,
      sousTitre: "Déclaration annuelle de récolte, dématérialisée obligatoire.",
      echeanceIso,
      jours,
      urgente: echeanceUrgente(jours),
      etat,
      faite: estFaite(etat),
      route: ROUTES_CONFORMITE.dr,
      ctaLabel: ctaSelonEtat(etat),
    });
  }

  // Tri chronologique : la plus proche (ou la plus en retard) d'abord.
  echeances.sort((a, b) => a.echeanceIso.localeCompare(b.echeanceIso));

  // `isLoading` d'une query désactivée vaut faux (react-query v5) — l'OR est sûr.
  const isLoading = drmQuery.isLoading || dsQuery.isLoading || drQuery.isLoading;

  return { echeances, isLoading };
}
